import makeStyles from '@mui/styles/makeStyles';
import { Grid, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CRYPTO_EXPORT_ID,
  downloadCryptoExport,
  getCryptoExportFileName,
  useCryptoBreakdownQuery,
  type CryptoBreakdownPayload,
  type CryptoFundRow,
  type CryptoMancoSectionSummary,
  type CryptoSectionMetrics,
} from '../../../../api/rs-hg/crypto';
import { clientNameSelector } from '../../../../redux/auth/selectors';
import { raptorPlatformIdSelector } from '../../../../redux/raptorPlatform/selectors';
import {
  addComponentToPdfExport,
  removeComponentFromPdfExport,
} from '../../../../redux/fileExport/actions';
import { greys, mainColors, RaptorTheme } from '../../../../styling/theme';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import { PdfComponentType } from '../../../../types/redux/pdfExports/FileExportsStore';
import usePngFromComponent from '../../../../hooks/usePngFromComponent';
import { useFileExportMutation } from '../../../../hooks/useFileExportMutation';
import { specifyStatus } from '../../../../utilities/generalMappings';
import {
  addCommasToNumbersAndRound,
  percentageToTwoDecimalPlaces,
} from '../../../../utilities/numberFormatters';
import RaptorFileDownloadButton from '../../../feedback/RaptorFileDownloadButton.component';
import RaptorStatusBox from '../../../feedback/RaptorStatusBox.component';
import { GeneralOverviewPageRenderedComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import GridItem from '../../../layout/GridComponents/GridItem';
import DisplayAreaCenteredWrapper from '../../../layout/utilities/displayAreaWrapper';
import CustomTable from '../../../tables/CustomTable';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import MancoPieChart from '../emir/components/MancoPieChart/mancoPieChart.component';

const usePageStyles = makeStyles<RaptorTheme>(() => ({
  root: {
    transition: 'all .3s',
    '&:hover': {
      transform: 'scale(0.99)',
    },
  },
}));

const useSummaryStyles = makeStyles((theme: RaptorTheme) => ({
  regulationContainerRoot: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '0.62rem 1.88rem',
    paddingRight: 0,
    transition: 'all .3s',
    height: '100%',
    width: '100%',
    zIndex: 1,
    position: 'relative',
  },
  textSection: {
    display: 'flex',
    borderRadius: 4,
    flexDirection: 'column',
    justifyContent: 'space-around',
    width: '25%',
  },
  subTitle: {
    [theme.breakpoints.down('lg')]: {
      fontSize: '1.12rem',
    },
  },
  subTitleInfo: {
    [theme.breakpoints.down('lg')]: {
      fontSize: '1.12rem',
    },
    fontSize: '1.50rem',
    fontWeight: 'bold',
    marginBottom: '0.94rem',
    color: theme.palette.primary.main,
  },
  pieSection: {
    cursor: 'pointer',
    display: 'flex',
    borderRadius: 4,
    width: '75%',
    justifyContent: 'space-around',
  },
  pieRoot: {
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    paddingTop: '1.25rem',
    alignItems: 'center',
    justifyContent: 'flex-start',
    backgroundColor: 'white',
    borderRadius: '0.25rem',
    padding: '0 0.62rem',
    width: '9.38rem',
    '&:hover': {
      backgroundColor: greys.grey100,
    },
  },
  pie: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  percentageText: {
    fontSize: '1.12rem',
    fontWeight: 500,
    color: mainColors.mainBlue,
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
  },
  pieTitle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    fontSize: '1.25rem',
    textAlign: 'center',
    width: '100%',
    padding: '0.12rem 0.31rem',
    fontWeight: 500,
    color: mainColors.mainBlue,
    backgroundColor: 'transparent',
    borderRadius: '0.25rem',
  },
}));

const useFundStyles = makeStyles(() => ({
  fundContainerRoot: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    padding: '0.62rem 1.88rem',
    transition: 'all .3s',
    height: '100%',
    width: '100%',
    zIndex: 1,
  },
  title: {
    display: 'flex',
    borderRadius: 4,
    flexDirection: 'column',
    justifyContent: 'space-around',
    width: '25%',
    margin: '0.62rem 0',
  },
  fundLevelTable: {
    border: `1px solid ${mainColors.lightGrey}`,
    borderRadius: '0.25rem',
    margin: '1.25rem 0',
  },
}));

const TABLE_OPTIONS = {
  paging: false,
  search: false,
  exportButton: true,
};

type CryptoFundTableRow = {
  fundId: string;
  fundName: string;
  limit: string;
  value: string;
  percentageValue: string;
  status: string;
  index: number;
};

function latestReturnedPositionDate(
  dates: readonly string[] | null | undefined,
): string | null {
  const present = dates?.filter(Boolean) ?? [];
  if (!present.length) {
    return null;
  }
  return present.reduce((latest, date) => (date > latest ? date : latest));
}

function sectionMetrics(
  fund: CryptoFundRow,
  sectionKey: string,
): CryptoSectionMetrics | undefined {
  if (sectionKey === 'direct_crypto') return fund.direct_crypto;
  if (sectionKey === 'indirect_crypto') return fund.indirect_crypto;
  return undefined;
}

function CryptoPie({
  section,
  isSelected,
  onSelect,
}: {
  section: CryptoMancoSectionSummary;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const classes = useSummaryStyles();
  const pieData = [
    { name: 'Pass', value: section.pass },
    { name: 'Fail', value: section.fail },
  ];

  return (
    <Tooltip title={`Filter ${section.label}`}>
      <div className={classes.pieRoot} onClick={onSelect}>
        <div className={classes.pie}>
          <MancoPieChart data={pieData} animBegin={0} />
          <div className={classes.percentageText}>
            {Math.round(section.percentage_pass * 100)}%
          </div>
        </div>
        <div
          className={classes.pieTitle}
          style={{
            backgroundColor: isSelected
              ? mainColors.mainBlue_slightlyDarker
              : 'transparent',
            color: isSelected ? 'white' : mainColors.mainBlue,
          }}
        >
          {section.label}
        </div>
      </div>
    </Tooltip>
  );
}

function fundLevelColumns(
  clientName: string | null,
): CustomColumn<CryptoFundTableRow>[] {
  return [
    {
      title: 'Fund Id',
      field: 'fundId',
      cellStyle: { textAlign: 'center' },
      headerStyle: { textAlign: 'center' },
      render: (rowData) =>
        clientName === 'mersenne' ? `fund${rowData.index}` : rowData.fundId,
    },
    {
      title: 'Fund Name',
      field: 'fundName',
      cellStyle: { textAlign: 'left' },
      headerStyle: { textAlign: 'center' },
    },
    {
      title: 'Limit',
      field: 'limit',
      cellStyle: { textAlign: 'center' },
      headerStyle: { textAlign: 'center' },
    },
    {
      title: 'Value',
      field: 'value',
      cellStyle: { textAlign: 'center' },
      headerStyle: { textAlign: 'center' },
    },
    {
      title: 'Percentage',
      field: 'percentageValue',
      cellStyle: { textAlign: 'center' },
      headerStyle: { textAlign: 'center' },
    },
    {
      title: 'Status',
      field: 'status',
      cellStyle: { textAlign: 'center' },
      width: '15%',
      render: (rowData) => (
        <RaptorStatusBox status={specifyStatus(rowData.status)} />
      ),
      headerStyle: { textAlign: 'center' },
    },
  ];
}

const CryptoPage: React.FC<GeneralOverviewPageRenderedComponentProps> = ({
  positionDate,
}) => {
  const pageClasses = usePageStyles();
  const summaryClasses = useSummaryStyles();
  const fundClasses = useFundStyles();
  const clientName = useSelector(clientNameSelector) || 'mersenne';
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);
  const cryptoQuery = useCryptoBreakdownQuery(positionDate);
  const { mutate: downloadCrypto, status: cryptoExportStatus } =
    useFileExportMutation({
      mutationKey: [CRYPTO_EXPORT_ID, positionDate, raptorPlatformId],
      download: downloadCryptoExport,
      getFileName: getCryptoExportFileName,
      errorMessage: 'Crypto export failed. Please try again.',
    });
  const isCryptoExportDisabled = cryptoExportStatus !== 'idle';
  const dispatch = useDispatch();
  const { ref: summaryRef, handleDownload } = usePngFromComponent();
  const [selectedKey, setSelectedKey] = useState('');

  useEffect(() => {
    setSelectedKey('');
  }, [positionDate]);

  useEffect(() => {
    dispatch(
      addComponentToPdfExport({
        identifier: 'crypto-manco-summary',
        handler: handleDownload,
        type: PdfComponentType.GENERAL_COMPONENT_IMAGE,
        title: '',
        dontMoveToNewPage: true,
        specificYPosition: undefined,
        specificXPosition: undefined,
        alignment: undefined,
        dontIncrementYPosition: false,
        groupOrder: 0,
      }),
    );
    return () => {
      dispatch(removeComponentFromPdfExport('crypto-manco-summary'));
    };
  }, [dispatch, handleDownload]);

  const columns = useMemo(() => fundLevelColumns(clientName), [clientName]);

  return (
    <QueryBoundary<CryptoBreakdownPayload>
      isLoading={cryptoQuery.isLoading}
      isFetching={cryptoQuery.isFetching}
      isError={cryptoQuery.isError}
      error={cryptoQuery.error}
      data={cryptoQuery.data?.data}
      loadingMessages={[
        'Loading Manco Level Data',
        'Manco Level Data May Take a Few Moments to Calculate',
        '...',
      ]}
      errorMessage="There was an error retrieving Crypto data."
      noDataMessage="No Crypto data available."
    >
      {(payload) => {
        const returnedPositionDate = latestReturnedPositionDate(
          cryptoQuery.data?.returned_position_dates,
        );
        const fundsCount =
          cryptoQuery.data?.funds.length ?? payload.funds.length;
        const selectedSection = payload.manco_section_summaries.find(
          (section) => section.key === selectedKey,
        );
        const fundRows: CryptoFundTableRow[] = [];
        payload.funds.forEach((fund, index) => {
          const section = sectionMetrics(fund, selectedKey);
          if (!section) return;
          fundRows.push({
            fundId: fund.fund_name,
            fundName: fund.fund_name_full,
            limit: percentageToTwoDecimalPlaces(section.limit),
            value: addCommasToNumbersAndRound(section.value),
            percentageValue: percentageToTwoDecimalPlaces(
              section.percentage_value,
            ),
            status: section.status,
            index: index + 1,
          });
        });

        return (
          <Grid container spacing={2} style={{ padding: 8 }}>
            {positionDate ? (
              <GridItem
                card
                key="export"
                item
                xs={12}
                cardStyle={{ padding: '0.62rem' }}
              >
                <div className="flex w-[31.25rem] flex-col gap-2.5 rounded border-2 border-[#f1f1f1] bg-white p-2.5 shadow-[0px_2px_5px_#f1f1f1]">
                  <div className="text-xl font-semibold text-mainblue">
                    Crypto Export (.xlsx)
                  </div>
                  <div
                    className={`my-[0.31rem] w-[12.5rem] ${
                      isCryptoExportDisabled ? 'pointer-events-none' : ''
                    }`}
                    onClick={() => {
                      if (isCryptoExportDisabled) {
                        return;
                      }
                      downloadCrypto({ positionDate, raptorPlatformId });
                    }}
                  >
                    <RaptorFileDownloadButton status={cryptoExportStatus} />
                  </div>
                </div>
              </GridItem>
            ) : null}
            <GridItem
              card
              key="manco-overview"
              item
              classes={pageClasses}
              cardStyle={{ height: '18.75rem' }}
              xs={12}
            >
              <div
                className={summaryClasses.regulationContainerRoot}
                ref={summaryRef}
              >
                <div className={summaryClasses.textSection}>
                  <Typography variant="h1" style={{ borderBottom: '0.62rem' }}>
                    Manco Summary
                  </Typography>
                  <div>
                    <Typography
                      variant="h2"
                      className={summaryClasses.subTitle}
                    >
                      Number of Sub-Funds:
                    </Typography>
                    <Typography
                      variant="h3"
                      className={summaryClasses.subTitleInfo}
                    >
                      {fundsCount}
                    </Typography>
                    {returnedPositionDate ? (
                      <>
                        <Typography
                          variant="h2"
                          className={summaryClasses.subTitle}
                        >
                          Position Date:
                        </Typography>
                        <Typography
                          variant="h3"
                          className={summaryClasses.subTitleInfo}
                        >
                          {returnedPositionDate}
                        </Typography>
                      </>
                    ) : null}
                  </div>
                </div>
                <div className={summaryClasses.pieSection}>
                  {payload.manco_section_summaries
                    .filter((section) => section.pass + section.fail > 0)
                    .map((section) => (
                      <CryptoPie
                        key={section.key}
                        section={section}
                        isSelected={selectedKey === section.key}
                        onSelect={() => setSelectedKey(section.key)}
                      />
                    ))}
                </div>
              </div>
            </GridItem>
            <GridItem
              card
              key="fund-overview"
              item
              cardStyle={{ minHeight: '18.75rem' }}
              xs={12}
            >
              {selectedSection ? (
                <div className={fundClasses.fundContainerRoot}>
                  <div className={fundClasses.title}>
                    <Typography variant="h1">
                      {selectedSection.label} Data
                    </Typography>
                  </div>
                  <div className={fundClasses.fundLevelTable}>
                    <CustomTable<CryptoFundTableRow>
                      columns={columns}
                      showToolbar
                      data={fundRows}
                      title="Fund Level Data"
                      exportFileName={
                        positionDate
                          ? `Crypto_fund_level-${positionDate}`
                          : undefined
                      }
                      options={TABLE_OPTIONS}
                      pdfDontMoveToNewPage={true}
                    />
                  </div>
                </div>
              ) : (
                <DisplayAreaCenteredWrapper noMinHeight>
                  <Typography variant="h1">
                    Please Click on a Chart Above to Show Fund Data.
                  </Typography>
                </DisplayAreaCenteredWrapper>
              )}
            </GridItem>
          </Grid>
        );
      }}
    </QueryBoundary>
  );
};

export default CryptoPage;
